// MagnusQueryHandlers.tsx - Extracted query handlers to reduce complexity
// ============================================================

import { PfmeaItem } from '../types';

// Response type
export interface QueryResponse {
    answer: string;
    action?: { type: 'navigate'; target: string; label: string };
}

// Metrics type (simplified)
export interface MagnusMetrics {
    totalPfmeaItems: number;
    openActions: number;
    overdueActions: number;
    completedActions: number;
    byActionPriority: { high: number; medium: number; low: number };
    by4M: Record<string, number>;
}

// Page context type
export interface PageContext {
    name: string;
    purpose: string;
    nextSteps: string[];
    relatedPages: Array<{ path: string; label: string; why: string }>;
    aiagVdaStep: number[];
}

// ============================================================
// PFMEA KNOWLEDGE BASE
// ============================================================
const GREETINGS = [
    "Hey! I'm Magnus. I can help you navigate the PFMEA app or answer questions. What are you looking for?",
    "Hi! Need help with process failure analysis? I'm here to guide you!",
    "Welcome! Looking for a specific page or have PFMEA questions? Just ask!",
];

const KNOWLEDGE_RESPONSES: Record<string, QueryResponse> = {
    workflow: {
        answer: `The **AIAG-VDA 7-Step PFMEA** workflow is:

1. **Planning & Preparation** - Define scope, team, timing
2. **Structure Analysis** - Define process elements (Process Flow)
3. **Function Analysis** - Define what each step does
4. **Failure Analysis** - Identify failure modes, effects, causes
5. **Risk Analysis** - Rate Severity, Occurrence, Detection
6. **Optimization** - Define actions to reduce risk
7. **Documentation** - Control Plans, Work Instructions

I can take you to any step. Which one do you need?`,
        action: { type: 'navigate', target: '/', label: 'View Workflow Progress' },
    },
    fourM: {
        answer: `The **4M categories** help classify failure causes:

👤 **Man** - Operator skills, training, fatigue, errors
⚙️ **Machine** - Equipment capability, maintenance, calibration
📦 **Material** - Raw material variation, supplier quality
📋 **Method** - Process parameters, work instructions, procedures

Each PFMEA item should have its cause categorized by 4M. This helps target improvements!`,
    },
    severity: {
        answer: `**Severity** rates how bad the failure effect is (1-10):

• **10** - May endanger operator without warning
• **9** - May endanger with warning
• **8** - Loss of primary function (total)
• **7** - Loss of primary function (degraded)
• **5-6** - Secondary function loss
• **2-4** - Minor/negligible impact
• **1** - No effect

Severity can only be reduced by **design changes**. It relates to the failure EFFECT, not the cause.`,
    },
    occurrence: {
        answer: `**Occurrence** rates how likely the cause will happen (1-10):

• **10** - Very high (≥100 per 1000)
• **7-9** - High (10-50 per 1000)
• **4-6** - Moderate (0.1-10 per 1000)
• **2-3** - Low/Very low (<0.1 per 1000)
• **1** - Almost impossible

Reduce occurrence with **prevention controls**: better process parameters, poka-yoke, SPC.`,
    },
    detection: {
        answer: `**Detection** rates how likely you'll catch the failure before it reaches the customer (1-10):

• **10** - No detection possible
• **7-9** - Low chance of detection
• **4-6** - Moderate (automated detection, SPC)
• **2-3** - High (in-station detection, error-proofing)
• **1** - Almost certain (mistake-proofing)

Lower detection with **detection controls**: inspections, tests, sensors, poka-yoke.`,
    },
    actionPriority: {
        answer: `**Action Priority (AP)** replaces RPN in AIAG-VDA:

🔴 **H (High)** - Immediate action required
🟡 **M (Medium)** - Action should be taken
🟢 **L (Low)** - Action may be taken

AP is based on the **combination** of S, O, and D:
• Severity 9-10 with high O or D → **H**
• Severity 7-8 with moderate O and D → **M**
• Low severity or well-controlled → **L**

Focus on **H** items first!`,
        action: { type: 'navigate', target: '/pfmea', label: 'View High Priority Items' },
    },
    specialChar: {
        answer: `**Special Characteristics** are critical features:

🔴 **CC (Critical Characteristic)** - Safety or regulatory. Requires 100% inspection or mistake-proofing.

🟡 **SC (Significant Characteristic)** - Important for fit/function. Requires SPC or increased sampling.

Special characteristics flow from PFMEA to Control Plan - they determine inspection frequency!`,
    },
    controlPlan: {
        answer: `The **Control Plan** documents how you'll control the process:

• Process/Product Characteristics
• Specifications and tolerances
• Evaluation techniques (gages, instruments)
• Sample size and frequency
• Control method (SPC, inspection, poka-yoke)
• Reaction plan when out of control

Control Plans are generated from PFMEA detection controls. High severity = more inspection!`,
        action: { type: 'navigate', target: '/control-plan', label: 'Go to Control Plan' },
    },
    functionMatrix: {
        answer: `The **Function Matrix** (AIAG-VDA Step 3) links process steps to product requirements:

✅ Shows which steps impact which requirements
✅ Identifies gaps in function coverage  
✅ Prepares for failure analysis

It's a visual "who does what" map showing how your process delivers customer requirements. Each checkbox links a step to a requirement it fulfills!`,
        action: { type: 'navigate', target: '/function-matrix', label: 'Go to Function Matrix' },
    },
    projectScoping: {
        answer: `**Project Scoping** uses the **5T Framework** (AIAG-VDA Step 1):

📋 **InTent** - Goals, scope, assumptions, exclusions
⏱️ **Timing** - Kickoff, milestones, completion dates
👥 **Team** - Members, roles, team lead, cross-functional
🛠️ **Tools** - Software, templates, standards, methods
✅ **Tasks** - Work breakdown, owners, due dates, status

This ensures proper PFMEA planning and preparation!`,
        action: { type: 'navigate', target: '/project-scoping', label: 'Go to 5T Dashboard' },
    },
    failureNet: {
        answer: `The **Failure Net** shows the complete AIAG-VDA failure propagation chain:

1️⃣ **Cause** (4M: Man/Machine/Material/Method)
2️⃣ **Failure Mode** (What goes wrong)
3️⃣ **Local Effect** (Immediate impact)
4️⃣ **Next-Level Effect** (Downstream impact)
5️⃣ **End Effect** (Customer/safety impact)

Click any node to highlight the full failure path! Prevention controls link to causes, detection controls to modes. It's a visual map of how failures propagate!`,
        action: { type: 'navigate', target: '/failure-net', label: 'Go to Failure Net' },
    },
    workflowDashboard: {
        answer: `The **Workflow Dashboard** tracks your PFMEA approval process:

✅ Draft items ready for review
🔍 Items in review status
✔️ Approved items with signatures

You can submit items for review, approve/reject pending items, and view complete revision history. Step 7 of AIAG-VDA!`,
        action: { type: 'navigate', target: '/workflow', label: 'Go to Workflow Dashboard' },
    },
    actionTracker: {
        answer: `The **Action Tracker** manages PFMEA improvement actions:

✅ Task assignment and ownership
📅 Due dates with overdue alerts
📊 Risk reduction % from revised S/O/D
✔️ Completion tracking

Each action links back to its PFMEA item. You can filter by status: Open, Overdue, Completed!`,
        action: { type: 'navigate', target: '/actions', label: 'Go to Action Tracker' },
    },
};

// Navigation shortcuts
const NAVIGATION_SHORTCUTS: Record<string, QueryResponse> = {
    'dashboard': { answer: "**Dashboard** shows your PFMEA metrics, workflow progress, and quick actions.", action: { type: 'navigate', target: '/', label: 'Go to Dashboard' } },
    'process-flow': { answer: "**Process Flow Diagram** is where you define manufacturing steps (OP-010, OP-020...) with visual symbols.", action: { type: 'navigate', target: '/process-flow', label: 'Go to Process Flow' } },
    'pfmea': { answer: "**PFMEA Table/Editor** shows all your failure modes with inline editing, S/O/D ratings, and AI suggestions.", action: { type: 'navigate', target: '/pfmea', label: 'Go to PFMEA Editor' } },
    'ideation': { answer: "**AI Ideation** uses Gemini AI to generate process steps and failure modes automatically.", action: { type: 'navigate', target: '/ideation', label: 'Go to AI Ideation' } },
    'control-plan': { answer: "**Control Plan** documents inspection methods, sample sizes, and reaction plans. Auto-generates from PFMEA!", action: { type: 'navigate', target: '/control-plan', label: 'Go to Control Plan' } },
    'settings': { answer: "**Settings** lets you choose your PFMEA standard (AIAG-VDA, AIAG V4, ISO 26262, etc.) and project.", action: { type: 'navigate', target: '/settings', label: 'Go to Settings' } },
    'function-matrix': { answer: "**Function Matrix** links process steps to product requirements in a visual grid (Step 3).", action: { type: 'navigate', target: '/function-matrix', label: 'Go to Function Matrix' } },
    'project-scoping': { answer: "**Project Scoping** is the 5T dashboard: InTent, Timing, Team, Tools, Tasks (Step 1).", action: { type: 'navigate', target: '/project-scoping', label: 'Go to Project Scoping' } },
    '5t': { answer: "**5T Dashboard** covers all planning: InTent, Timing, Team, Tools, Tasks.", action: { type: 'navigate', target: '/project-scoping', label: 'Go to 5T Dashboard' } },
    'failure-net': { answer: "**Failure Net** shows the 5-level failure chain with interactive D3.js graph visualization.", action: { type: 'navigate', target: '/failure-net', label: 'Go to Failure Net' } },
    'workflow': { answer: "**Workflow Dashboard** tracks Draft → Review → Approved status with signatures.", action: { type: 'navigate', target: '/workflow', label: 'Go to Workflow' } },
    'actions': { answer: "**Action Tracker** manages recommended actions with due dates and risk reduction tracking.", action: { type: 'navigate', target: '/actions', label: 'Go to Actions' } },
};

const FALLBACK = "I'm not sure about that. Try asking:\n• \"What is the 7-step workflow?\"\n• \"Explain the 4M categories\"\n• \"What is Action Priority?\"\n• \"Take me to the PFMEA table\"\n• \"What should I do next?\"\n\nFor deep analysis, enable **Prime Mode**!";

// ============================================================
// QUERY MATCHERS - Return true if query matches the pattern
// ============================================================
type QueryMatcher = (lower: string) => boolean;
type QueryHandler = (lower: string, context: PageContext | undefined, metrics: MagnusMetrics, pfmeaItems: PfmeaItem[]) => QueryResponse | null;

interface QueryRule {
    matcher: QueryMatcher;
    handler: QueryHandler;
}

// Helper to create simple knowledge-based handlers
const createKnowledgeHandler = (key: keyof typeof KNOWLEDGE_RESPONSES): QueryHandler => {
    return () => KNOWLEDGE_RESPONSES[key];
};

// Query rules - ordered by specificity
const QUERY_RULES: QueryRule[] = [
    // Greetings
    {
        matcher: (q) => q === 'hi' || q === 'hello' || q === 'hey',
        handler: () => ({ answer: GREETINGS[Math.floor(Math.random() * GREETINGS.length)] }),
    },
    // Help
    {
        matcher: (q) => q === 'help' || q === 'help me',
        handler: () => ({
            answer: `I can help you with:\n\n• **Navigate** - "Take me to PFMEA table"\n• **PFMEA concepts** - "What is Action Priority?"\n• **Process** - "Explain the 7-step workflow"\n• **Your data** - "How many high-risk items?"\n\nFor deep analysis, toggle **Prime Mode**!`,
        }),
    },
    // What's next
    {
        matcher: (q) => q.includes('what') && (q.includes('next') || q.includes('do')),
        handler: (_, context) => {
            if (context) {
                return {
                    answer: `On **${context.name}**, you can:\n\n${context.nextSteps.map((s, i) => `${i + 1}. ${s}`).join('\n')}\n\n${context.relatedPages.length > 0 ? 'Related pages: ' + context.relatedPages.map(p => p.label).join(', ') : ''}`,
                };
            }
            return null;
        },
    },
    // Workflow
    {
        matcher: (q) => q.includes('7 step') || q.includes('7-step') || q.includes('workflow') || q.includes('aiag-vda'),
        handler: createKnowledgeHandler('workflow'),
    },
    // 4M
    {
        matcher: (q) => q.includes('4m') || q.includes('man machine') || q.includes('cause categor'),
        handler: createKnowledgeHandler('fourM'),
    },
    // Function Matrix
    {
        matcher: (q) => q.includes('function matrix') || q.includes('step 3') || q.includes('function analysis'),
        handler: createKnowledgeHandler('functionMatrix'),
    },
    // Project Scoping
    {
        matcher: (q) => q.includes('project scoping') || q.includes('5t') || q.includes('intent') || q.includes('planning preparation'),
        handler: createKnowledgeHandler('projectScoping'),
    },
    // Failure Net
    {
        matcher: (q) => q.includes('failure net') || q.includes('failure chain') || q.includes('propagation') || q.includes('visualization'),
        handler: createKnowledgeHandler('failureNet'),
    },
    // Workflow Dashboard
    {
        matcher: (q) => q.includes('workflow dashboard') || q.includes('approval') || q.includes('signature'),
        handler: createKnowledgeHandler('workflowDashboard'),
    },
    // Action Tracker
    {
        matcher: (q) => q.includes('action tracker') || q.includes('recommended action') || q.includes('step 6'),
        handler: createKnowledgeHandler('actionTracker'),
    },
    // Severity
    {
        matcher: (q) => q.includes('severity') || (q.includes('what is') && q.includes(' s ')),
        handler: createKnowledgeHandler('severity'),
    },
    // Occurrence
    {
        matcher: (q) => q.includes('occurrence') || (q.includes('what is') && q.includes(' o ')),
        handler: createKnowledgeHandler('occurrence'),
    },
    // Detection
    {
        matcher: (q) => q.includes('detection') || (q.includes('what is') && q.includes(' d ')),
        handler: createKnowledgeHandler('detection'),
    },
    // Action Priority
    {
        matcher: (q) => q.includes('action priority') || q.includes(' ap ') || q.includes('rpn'),
        handler: createKnowledgeHandler('actionPriority'),
    },
    // Special Characteristics
    {
        matcher: (q) => q.includes('special char') || q.includes(' cc ') || q.includes(' sc ') || q.includes('critical char'),
        handler: createKnowledgeHandler('specialChar'),
    },
    // Control Plan
    {
        matcher: (q) => q.includes('control plan'),
        handler: createKnowledgeHandler('controlPlan'),
    },
    // Data: How many items
    {
        matcher: (q) => q.includes('how many') && (q.includes('pfmea') || q.includes('item') || q.includes('failure')),
        handler: (_, __, metrics, pfmeaItems) => {
            const highPriorityText = metrics.byActionPriority.high > 0
                ? `\n\n⚠️ **${metrics.byActionPriority.high} items need immediate action!**\n\nTop risks:\n${pfmeaItems.filter(i => i.actionPriority === 'H').slice(0, 3).map(i => `• "${i.failureMode}" (S:${i.severity}, O:${i.occurrence}, D:${i.detection})`).join('\n')}`
                : '\n\n✅ **Great! No high-priority items.**';

            return {
                answer: `You have **${metrics.totalPfmeaItems} PFMEA items** across ${Object.keys(metrics.by4M).length || 4} 4M categories:\n\n• 🔴 **High Priority**: ${metrics.byActionPriority.high}\n• 🟡 **Medium Priority**: ${metrics.byActionPriority.medium}\n• 🟢 **Low Priority**: ${metrics.byActionPriority.low}${highPriorityText}`,
                action: metrics.byActionPriority.high > 0 ? { type: 'navigate', target: '/pfmea', label: 'View High Priority Items' } : undefined,
            };
        },
    },
    // Data: High risk
    {
        matcher: (q) => q.includes('high') && (q.includes('risk') || q.includes('priority')),
        handler: (_, __, ___, pfmeaItems) => {
            const highItems = pfmeaItems.filter(i => i.actionPriority === 'H');
            if (highItems.length === 0) {
                return { answer: "✅ **Excellent!** You have **no high-priority items**.\n\nYour process risks are well-controlled. Keep monitoring your medium-priority items!" };
            }
            return {
                answer: `You have **${highItems.length} high-priority items** requiring immediate action:\n\n${highItems.slice(0, 5).map((i, idx) => `${idx + 1}. **"${i.failureMode}"**\n   • Severity: ${i.severity}, Occurrence: ${i.occurrence}, Detection: ${i.detection}\n   • Process Step: ${i.processStepName}\n   • 4M Category: ${i.processElement}`).join('\n\n')}${highItems.length > 5 ? `\n\n...and ${highItems.length - 5} more` : ''}`,
                action: { type: 'navigate', target: '/pfmea', label: 'View All High Priority' },
            };
        },
    },
    // Data: Open actions
    {
        matcher: (q) => q.includes('open') && q.includes('action'),
        handler: (_, __, metrics) => ({
            answer: `You have **${metrics.openActions} open actions**${metrics.overdueActions > 0 ? ` (**${metrics.overdueActions} overdue** ⚠️)` : ' (none overdue ✅)'}.\n\n${metrics.completedActions > 0 ? `✅ **${metrics.completedActions} actions completed** so far.\n\n` : ''}${metrics.overdueActions > 0 ? 'Review overdue items first!' : 'Good progress!'}`,
            action: { type: 'navigate', target: '/actions', label: 'Go to Action Tracker' },
        }),
    },
];

// ============================================================
// MAIN EXPORTED FUNCTION
// ============================================================
export const handleBasicQuery = (
    query: string,
    context: PageContext | undefined,
    metrics: MagnusMetrics,
    pfmeaItems: PfmeaItem[],
): QueryResponse => {
    const lower = query.toLowerCase();

    // Check each rule in order
    for (const rule of QUERY_RULES) {
        if (rule.matcher(lower)) {
            const result = rule.handler(lower, context, metrics, pfmeaItems);
            if (result) return result;
        }
    }

    // Check navigation shortcuts
    for (const [key, value] of Object.entries(NAVIGATION_SHORTCUTS)) {
        if (lower.includes(key) || lower.includes(key.replace('-', ' '))) {
            return value;
        }
    }

    // Fallback
    return { answer: FALLBACK };
};
